import { canReadProperties, coreQueryKey } from '@/api/core/authorization';
import { readNativeAccessInventory } from '@/api/core/native-access-inventory';
import { useOperatorSession } from '@/auth/runtime';
import { LoadFailure } from '@/components/load-failure';
import { formatDate, formatDateTimeWithOffset } from '@/lib/format';
import { Badge, Button } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DevicePurposeEditor } from '../access/device-purpose-editor';
import { propertyRead, type PropertyRuntime } from './property-queries';

/** Property-scoped saved links; provider application remains an independent observation. */
export function PropertyAccessOverview({
  runtime,
  propertyId,
  generation,
  onRoom,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  generation: number;
  onRoom: (room: string) => void;
}) {
  const session = useOperatorSession(runtime.session);
  const allowed =
    session.status === 'authorized' &&
    session.generation === generation &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    canReadProperties(session.authorization);
  const [cursor, setCursor] = useState<string | null>(null);
  const current = () => runtime.session.getSnapshot().generation === generation;
  const query = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-access-inventory', propertyId),
      cursor,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        generation,
        () => readNativeAccessInventory(runtime.client, propertyId, cursor, signal),
        signal,
        current,
      ),
    enabled: allowed,
    retry: false,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  if (!allowed) return null;
  return (
    <section className="space-y-4" aria-label="지점 출입 연결">
      <h3 className="font-semibold">연결된 문과 사용 위치</h3>
      <p className="body-sm text-muted-foreground">이 지점에 저장된 문 연결과 사용 위치입니다.</p>
      <Badge variant="outline">실기기 반영 미확인</Badge>
      {query.isPending && <p role="status">출입 연결을 불러오고 있습니다.</p>}
      {query.isError && (
        <LoadFailure subject="출입 연결" error={query.error} onRetry={() => void query.refetch()} />
      )}
      {query.data && (
        <>
          <div className="body-sm text-muted-foreground">
            <p>조회일 {formatDate(query.data.observed_at)}</p>
            <details>
              <summary className="cursor-pointer">조회 시각 자세히</summary>
              <p>{formatDateTimeWithOffset(query.data.observed_at)}</p>
            </details>
          </div>
          {!query.data.core_slots.length && (
            <p>
              이 페이지에 조회된 슬롯 연결이 없습니다. 출입 그룹의 존재 여부는 별도 확인이
              필요합니다.
            </p>
          )}
          <ul className="space-y-3">
            {query.data.core_slots.map((slot) => {
              const link = query.data.items.find((item) => item.slot_id === slot.slot_id);
              const device = link?.device;
              return (
                <li key={slot.slot_id} className="space-y-2 rounded-md border p-4">
                  <h4 className="font-medium">
                    {slot.room_name ?? '객실명 미설정'} / {slot.name ?? '슬롯명 미설정'}
                  </h4>
                  <p>
                    {link
                      ? {
                          CURRENT: '현재 연결',
                          NO_MAPPING: '연결 없음',
                          UNCERTAIN: '관계 확인 필요',
                          UNCONFIRMED: '장비 확인 필요',
                        }[link.state]
                      : '연결 응답 확인 실패'}
                  </p>
                  {device && (
                    <p className="body-sm">
                      {device.device_type === 'DOOR'
                        ? '출입문'
                        : device.device_type === 'LOCK'
                          ? '잠금장치'
                          : device.device_type === null
                            ? '장비 분류 미설정'
                            : '출입 장비'}{' '}
                      · {device.is_active ? '활성 설정' : '비활성 설정'}
                    </p>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      onRoom(slot.room_id);
                    }}
                  >
                    이 객실의 연결 설정
                  </Button>
                  {device?.core_device_id && (
                    <details>
                      <summary className="cursor-pointer">장비 용도 확인·수정</summary>
                      <DevicePurposeEditor
                        runtime={runtime}
                        id={device.core_device_id}
                        propertyId={propertyId}
                      />
                    </details>
                  )}
                </li>
              );
            })}
          </ul>
          {query.data.next_cursor && (
            <Button
              disabled={query.isFetching}
              variant="outline"
              onClick={() => {
                setCursor(query.data.next_cursor);
              }}
            >
              다음 연결
            </Button>
          )}
          {cursor && (
            <Button
              disabled={query.isFetching}
              variant="outline"
              onClick={() => {
                setCursor(null);
              }}
            >
              처음 연결
            </Button>
          )}
        </>
      )}
    </section>
  );
}
